/** The two languages this product ships. Spanish is the default; English is switchable. */
export const LOCALES = ['es', 'en'];
/** Spanish, per the brief. `DEFAULT_LOCALE` in `.env` may not name a locale outside `LOCALES`. */
export const DEFAULT_LOCALE = 'es';
/** Narrows an arbitrary string, so a header or a setting can be validated rather than trusted. */
export function isLocale(value) {
    return LOCALES.includes(value);
}
/**
 * The only names a message may interpolate.
 *
 * Every entry is a number, a duration, a limit or a schema identifier. Nothing here can carry a
 * person's name, a document number, a telephone number, an email address or a pass code, and
 * nothing may be added that can. A date is deliberately absent: a date in an error message is
 * usually a date of birth or a document expiry, both of which are personal data.
 *
 * The list is an allowlist and not a wishlist: a test asserts that every entry is actually used by
 * at least one message, so a name cannot be pre-approved here and quietly put to work later.
 */
export const PERMITTED_PLACEHOLDERS = [
    'attempts',
    'count',
    'credentialType',
    'direction',
    'fieldKey',
    'fieldPath',
    'hours',
    'limit',
    'max',
    'maxLength',
    'maxMb',
    'min',
    'minLength',
    'minutes',
    'retryAfterSeconds',
    'role',
    'schemaVersion',
    'scope',
];
/**
 * i18next's default interpolation syntax. Written as a source string and compiled per call rather
 * than shared, because a global regular expression carries `lastIndex` between calls and a shared
 * one would skip half the placeholders of every second message it was asked about.
 */
const PLACEHOLDER_SOURCE = '\\{\\{\\s*([A-Za-z]\\w*)\\s*\\}\\}';
/**
 * Every placeholder a message interpolates, in order of appearance and without duplicates.
 *
 * Order is preserved rather than sorted so a mismatch reads the way the translator wrote it, and
 * duplicates are dropped so `{{count}} of {{count}}` compares equal to `{{count}}` in the other
 * language, which is a legitimate difference between two languages rather than a defect.
 */
export function placeholdersOf(message) {
    const found = [];
    for (const match of message.matchAll(new RegExp(PLACEHOLDER_SOURCE, 'gu'))) {
        const name = match[1];
        if (name !== undefined && !found.includes(name)) {
            found.push(name);
        }
    }
    return found;
}
/** Whether a placeholder name is one a message is allowed to carry. */
export function isPermittedPlaceholder(name) {
    return PERMITTED_PLACEHOLDERS.includes(name);
}
/**
 * Substitutes the allowlisted placeholders in a message.
 *
 * A placeholder with no value is left standing rather than replaced with an empty string: an
 * operator seeing `{{minutes}}` knows a value is missing, where a message reading "try again in
 * minutes" reads as finished prose and hides the defect.
 */
export function interpolate(message, params = {}) {
    return message.replace(new RegExp(PLACEHOLDER_SOURCE, 'gu'), (whole, rawName) => {
        if (!isPermittedPlaceholder(rawName)) {
            return whole;
        }
        const value = params[rawName];
        return value === undefined ? whole : String(value);
    });
}
//# sourceMappingURL=keys.js.map